/**
 * Partner — Ledzerly's unified counterparty master.
 *
 * One master per real-world counterparty, carrying every role that counterparty
 * plays. A single business can be a customer, a vendor and a related party at
 * once; Tally and Zoho force you to maintain these as separate ledgers, which
 * is where duplicate masters and unreconciled balances come from.
 *
 * One Partner, many roles, one GSTIN, one PAN, one ageing view.
 *
 * Note on naming: "Partner" here means a trading counterparty, not a partner of
 * a firm and not a partner in a partnership entity. Where the capital section
 * of the chart of accounts refers to partners of the business, that concept
 * lives in the entity profile, not here.
 */

export type PartnerRole =
  | "customer"
  | "vendor"
  | "employee"
  | "related-party"
  | "bank"
  | "statutory"
  | "director"
  | "firm-partner";

export type PartnerKind = "business" | "individual" | "government" | "trust";

/** GST registration status of the counterparty — drives place-of-supply logic. */
export type PartnerGstStatus =
  | "regular"
  | "composition"
  | "unregistered"
  | "sez"
  | "overseas"
  | "uin"
  | "deemed-export";

/** MSME classification under the MSMED Act 2006 (as amended 2020). */
export type MsmeCategory = "micro" | "small" | "medium" | "not-registered" | "not-applicable";

export interface PartnerMsme {
  category: MsmeCategory;
  /** Udyam Registration Number, format UDYAM-XX-00-0000000. */
  udyamNumber?: string;
  registeredOn?: string;
  /**
   * Agreed credit period in days. Section 15 of the MSMED Act caps this at 45
   * days; beyond that, section 43B(h) disallows the expense until paid.
   */
  agreedCreditDays?: number;
  verifiedOn?: string;
}

export interface PartnerAddress {
  line1?: string;
  line2?: string;
  city?: string;
  /** Two-digit GST state code — drives CGST/SGST vs IGST. */
  stateCode?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

export interface PartnerContact {
  name?: string;
  designation?: string;
  email?: string;
  phone?: string;
}

export interface PartnerBank {
  accountName?: string;
  accountNumber?: string;
  ifsc?: string;
  bankName?: string;
  /** Verified through penny-drop before first payment. */
  verified?: boolean;
}

export interface Partner {
  id: string;
  /** Human-readable code, e.g. PTR-00042. */
  code: string;
  name: string;
  /** Trade name where it differs from the legal name. */
  legalName?: string;
  kind: PartnerKind;
  /** Every role this counterparty plays. A Partner may hold several. */
  roles: PartnerRole[];
  gstin?: string;
  gstStatus: PartnerGstStatus;
  pan?: string;
  tan?: string;
  cin?: string;
  msme?: PartnerMsme;
  billingAddress?: PartnerAddress;
  shippingAddress?: PartnerAddress;
  contacts?: PartnerContact[];
  bank?: PartnerBank;
  /** Default TDS section applied to purchases from this Partner. */
  defaultTdsSection?: string;
  /** Purchases from this Partner attract reverse charge. */
  rcmApplicable?: boolean;
  /** Credit limit in base currency; blocks new invoices when exceeded. */
  creditLimit?: number;
  creditDays?: number;
  currency?: string;
  /** Control accounts this Partner posts to, by role. */
  receivableAccountCode?: string;
  payableAccountCode?: string;
  active: boolean;
  notes?: string;
  createdAt: string;
  createdBy?: string;
}

/** GST state codes — first two digits of a GSTIN. */
export const GST_STATE_CODES: Record<string, string> = {
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh",
  "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh",
  "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal",
  "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh",
  "24": "Gujarat", "26": "Dadra and Nagar Haveli and Daman and Diu", "27": "Maharashtra",
  "29": "Karnataka", "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu",
  "34": "Puducherry", "35": "Andaman and Nicobar Islands", "36": "Telangana",
  "37": "Andhra Pradesh", "38": "Ladakh", "97": "Other Territory", "99": "Centre Jurisdiction"
};

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const TAN_PATTERN = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const UDYAM_PATTERN = /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/;

/**
 * GSTIN checksum, per the GSTN specification: characters are weighted
 * alternately 1 and 2 over the base-36 alphabet, digits summed with a
 * quotient-remainder fold, and the 15th character is the check digit.
 * Format alone is not enough — a typo'd GSTIN usually passes the regex.
 */
export const isValidGstinChecksum = (gstin: string): boolean => {
  if (!GSTIN_PATTERN.test(gstin)) return false;
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let total = 0;
  for (let index = 0; index < 14; index += 1) {
    const value = alphabet.indexOf(gstin[index]);
    if (value === -1) return false;
    const weighted = value * (index % 2 === 0 ? 1 : 2);
    total += Math.floor(weighted / 36) + (weighted % 36);
  }
  return alphabet[(36 - (total % 36)) % 36] === gstin[14];
};

export interface PartnerValidation {
  field: string;
  severity: "error" | "warning";
  message: string;
}

/** Validates a Partner before it is saved. Errors block; warnings inform. */
export const validatePartner = (partner: Partial<Partner>): PartnerValidation[] => {
  const issues: PartnerValidation[] = [];

  if (!partner.name?.trim()) {
    issues.push({ field: "name", severity: "error", message: "Name is required." });
  }
  if (!partner.roles?.length) {
    issues.push({ field: "roles", severity: "error", message: "Select at least one role — customer, vendor or another." });
  }

  if (partner.gstin) {
    const gstin = partner.gstin.toUpperCase().trim();
    if (!GSTIN_PATTERN.test(gstin)) {
      issues.push({ field: "gstin", severity: "error", message: "GSTIN must be 15 characters in the format 29ABCDE1234F1Z5." });
    } else if (!isValidGstinChecksum(gstin)) {
      issues.push({ field: "gstin", severity: "error", message: "GSTIN check digit does not match — verify the number on the GST portal." });
    } else {
      const stateCode = gstin.slice(0, 2);
      if (!GST_STATE_CODES[stateCode]) {
        issues.push({ field: "gstin", severity: "error", message: `${stateCode} is not a valid GST state code.` });
      }
      // The GSTIN embeds the PAN at positions 3-12. A mismatch means one of
      // the two is wrong, and it will break TDS and GST return reconciliation.
      if (partner.pan && gstin.slice(2, 12) !== partner.pan.toUpperCase().trim()) {
        issues.push({ field: "pan", severity: "error", message: "PAN does not match the PAN embedded in the GSTIN." });
      }
    }
  }

  if (partner.gstStatus === "regular" && !partner.gstin) {
    issues.push({ field: "gstin", severity: "error", message: "A regular GST registration requires a GSTIN." });
  }
  if (partner.gstStatus === "unregistered" && partner.gstin) {
    issues.push({ field: "gstin", severity: "warning", message: "GSTIN captured against an unregistered party — confirm the registration status." });
  }

  if (partner.pan && !PAN_PATTERN.test(partner.pan.toUpperCase().trim())) {
    issues.push({ field: "pan", severity: "error", message: "PAN must be 10 characters in the format ABCDE1234F." });
  }
  if (partner.tan && !TAN_PATTERN.test(partner.tan.toUpperCase().trim())) {
    issues.push({ field: "tan", severity: "error", message: "TAN must be 10 characters in the format BLRA12345B." });
  }

  // Without a PAN, TDS runs at 20% under section 206AA rather than the
  // section rate — worth flagging before the first bill is booked.
  if (!partner.pan && partner.roles?.includes("vendor")) {
    issues.push({ field: "pan", severity: "warning", message: "No PAN on record. TDS will apply at 20% under section 206AA." });
  }

  const msme = partner.msme;
  if (msme && msme.category !== "not-registered" && msme.category !== "not-applicable") {
    if (!msme.udyamNumber) {
      issues.push({ field: "msme", severity: "error", message: "An MSME classification requires the Udyam Registration Number." });
    } else if (!UDYAM_PATTERN.test(msme.udyamNumber.toUpperCase().trim())) {
      issues.push({ field: "msme", severity: "error", message: "Udyam number must look like UDYAM-KR-03-0001234." });
    }
    if (msme.agreedCreditDays !== undefined && msme.agreedCreditDays > 45) {
      issues.push({
        field: "msme",
        severity: "error",
        message: "Section 15 of the MSMED Act caps the agreed credit period at 45 days."
      });
    }
  }

  if (partner.bank?.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(partner.bank.ifsc.toUpperCase().trim())) {
    issues.push({ field: "bank", severity: "error", message: "IFSC must be 11 characters, e.g. HDFC0001234." });
  }

  return issues;
};

/** True when the Partner has a blocking validation error. */
export const partnerHasErrors = (issues: PartnerValidation[]): boolean =>
  issues.some((issue) => issue.severity === "error");

/** Derives the supply state from a GSTIN so place of supply is never guessed. */
export const stateFromGstin = (gstin?: string): { code: string; name: string } | undefined => {
  if (!gstin || gstin.length < 2) return undefined;
  const code = gstin.slice(0, 2);
  const name = GST_STATE_CODES[code];
  return name ? { code, name } : undefined;
};

/**
 * Intra-state supplies split CGST/SGST; inter-state attract IGST. Exports and
 * SEZ supplies are zero-rated and always treated as inter-state.
 */
export const supplyTypeFor = (
  supplierStateCode: string | undefined,
  partner: Pick<Partner, "gstin" | "gstStatus">
): "intra-state" | "inter-state" | "export" => {
  if (partner.gstStatus === "overseas") return "export";
  if (partner.gstStatus === "sez" || partner.gstStatus === "deemed-export") return "inter-state";
  const partyState = stateFromGstin(partner.gstin)?.code;
  if (!supplierStateCode || !partyState) return "intra-state";
  return supplierStateCode === partyState ? "intra-state" : "inter-state";
};

/**
 * MSME payment status under the actual-payment rule.
 *
 * Section 43B(h) of the 1961 Act became section 37(2)(g) of the Income-tax
 * Act, 2025 with effect from 1 April 2026. The substance is unchanged: an
 * expense to a micro or small enterprise is disallowed until actually paid
 * where payment runs past the agreed period, capped at 45 days by section 15
 * of the MSMED Act. Only the citation moves, so it is resolved by date.
 */
export interface MsmeDueStatus {
  applicable: boolean;
  dueDays: number;
  overdueDays: number;
  disallowable: boolean;
  message: string;
  /** The provision to cite, which depends on the year in question. */
  citation: string;
}

export const msmeDueStatus = (
  partner: Pick<Partner, "msme" | "roles">,
  invoiceDate: string,
  paidOn: string | undefined,
  asAt: string
): MsmeDueStatus => {
  const category = partner.msme?.category;
  const applicable =
    Boolean(partner.roles?.includes("vendor")) && (category === "micro" || category === "small");

  // The 2025 Act applies from 1 April 2026; earlier years stay under the 1961
  // Act by virtue of the section 536 saving clause.
  const citation = invoiceDate >= "2026-04-01"
    ? "section 37(2)(g) of the Income-tax Act, 2025"
    : "section 43B(h) of the Income-tax Act, 1961";

  if (!applicable) {
    return { applicable: false, dueDays: 0, overdueDays: 0, disallowable: false, message: `The actual-payment rule under ${citation} does not apply.`, citation };
  }

  // Where no period is agreed in writing, section 15 sets it at 15 days.
  const agreed = partner.msme?.agreedCreditDays;
  const dueDays = agreed === undefined ? 15 : Math.min(agreed, 45);

  const start = new Date(invoiceDate).getTime();
  const end = new Date(paidOn ?? asAt).getTime();
  const elapsed = Math.floor((end - start) / 86_400_000);
  const overdueDays = Math.max(0, elapsed - dueDays);

  return {
    applicable: true,
    dueDays,
    overdueDays,
    disallowable: overdueDays > 0 && !paidOn,
    citation,
    message: paidOn
      ? overdueDays > 0
        ? `Paid ${overdueDays} day(s) beyond the ${dueDays}-day limit. Allowable in the year of payment.`
        : `Paid within the ${dueDays}-day limit. Fully allowable.`
      : overdueDays > 0
        ? `Unpaid and ${overdueDays} day(s) past the ${dueDays}-day limit. Disallowable under ${citation} until paid.`
        : `Within the ${dueDays}-day limit. ${dueDays - elapsed} day(s) remaining.`
  };
};

export const ROLE_LABELS: Record<PartnerRole, string> = {
  customer: "Customer",
  vendor: "Vendor",
  employee: "Employee",
  "related-party": "Related party",
  bank: "Bank",
  statutory: "Statutory authority",
  director: "Director",
  "firm-partner": "Firm partner"
};

export const GST_STATUS_LABELS: Record<PartnerGstStatus, string> = {
  regular: "Regular",
  composition: "Composition",
  unregistered: "Unregistered",
  sez: "SEZ",
  overseas: "Overseas",
  uin: "UIN holder",
  "deemed-export": "Deemed export"
};

export const MSME_LABELS: Record<MsmeCategory, string> = {
  micro: "Micro",
  small: "Small",
  medium: "Medium",
  "not-registered": "Not registered",
  "not-applicable": "Not applicable"
};

/** Normalises user input before save — uppercase identifiers, trim everything. */
export const normalisePartner = (partner: Partner): Partner => ({
  ...partner,
  name: partner.name.trim(),
  legalName: partner.legalName?.trim(),
  gstin: partner.gstin?.toUpperCase().trim() || undefined,
  pan: partner.pan?.toUpperCase().trim() || undefined,
  tan: partner.tan?.toUpperCase().trim() || undefined,
  cin: partner.cin?.toUpperCase().trim() || undefined,
  msme: partner.msme
    ? { ...partner.msme, udyamNumber: partner.msme.udyamNumber?.toUpperCase().trim() || undefined }
    : undefined,
  bank: partner.bank ? { ...partner.bank, ifsc: partner.bank.ifsc?.toUpperCase().trim() || undefined } : undefined
});

/** Creates a blank Partner pre-filled for a role — used by inline quick-create. */
export const blankPartner = (name: string, role: PartnerRole, code: string, id: string): Partner => ({
  id,
  code,
  name: name.trim(),
  kind: "business",
  roles: [role],
  gstStatus: "unregistered",
  currency: "INR",
  active: true,
  msme: { category: "not-registered" },
  createdAt: new Date().toISOString()
});
